import "./Users.scss";
import {
  Button,
  Empty,
  Grid,
  Input,
  Pagination,
  Table,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import {
  CalendarOutlined,
  EditOutlined,
  EyeOutlined,
  HistoryOutlined,
  PlusOutlined,
  StopOutlined,
  UserAddOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import {
  fetchData,
  fixedTableScrollY,
  Notify,
  rowsPerPage,
} from "../../utils/constants";
import { useCallback, useEffect, useMemo, useState } from "react";
import { User } from "../../utils/types";
import UserModal from "../Modals/UserModal";
import ViewUserDetailsModal from "../Modals/ViewUserDetailsModal";
import TerminateEmploymentModal from "../Modals/TerminateEmploymentModal";
import RehireModal from "../Modals/RehireModal";
import EmploymentHistoryModal from "../Modals/EmploymentHistoryModal";
import { useUser } from "../../context/UserContext";
import {
  DEFAULT_EMPLOYEE_STATUS_FILTER,
  EmployeeStatusFilter,
  EmployeeStatusFilterValue,
  matchesEmployeeStatus,
} from "../Filters/EmployeeStatusFilter";

type UserSearchFilters = {
  employeeCode: string;
  firstName: string;
  lastName: string;
  email: string;
  managerFullName: string;
};

const DEFAULT_USERS_PAGE_SIZE = rowsPerPage;
const USERS_PAGE_SIZE_OPTIONS = ["5", "10", "20", "50"];
const INITIAL_SEARCH_FILTERS: UserSearchFilters = {
  employeeCode: "",
  firstName: "",
  lastName: "",
  email: "",
  managerFullName: "",
};

const Users = () => {
  const { authUser } = useUser();
  const screens = Grid.useBreakpoint();
  const isDesktop = !!screens.md;
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [isViewUserModalOpen, setIsViewUserModalOpen] =
    useState<boolean>(false);
  const [isTerminateModalOpen, setIsTerminateModalOpen] =
    useState<boolean>(false);
  const [isRehireModalOpen, setIsRehireModalOpen] = useState<boolean>(false);
  const [isHistoryModalOpen, setIsHistoryModalOpen] = useState<boolean>(false);
  const [users, setUsers] = useState<User[]>([]);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_USERS_PAGE_SIZE);
  const [searchFilters, setSearchFilters] = useState<UserSearchFilters>(
    INITIAL_SEARCH_FILTERS,
  );
  const [activeFilter, setActiveFilter] = useState<EmployeeStatusFilterValue>(
    DEFAULT_EMPLOYEE_STATUS_FILTER,
  );

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      await fetchData(
        `${process.env.REACT_APP_API_URL}/users/displayAllUsers`,
        setUsers,
      );
    } catch (error) {
      console.error("Failed to fetch users data:", error);
      Notify("error", "Could not load users data. Please try again.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!authUser) return;
    fetchUsers();
  }, [authUser, fetchUsers]);

  const handleSearchFilterChange = useCallback(
    (field: keyof UserSearchFilters, value: string) => {
      setSearchFilters((previousFilters) => ({
        ...previousFilters,
        [field]: value,
      }));
    },
    [],
  );

  const filteredSortedUsers = useMemo(() => {
    const normalizedFilters = {
      employeeCode: searchFilters.employeeCode.trim().toLowerCase(),
      firstName: searchFilters.firstName.trim().toLowerCase(),
      lastName: searchFilters.lastName.trim().toLowerCase(),
      email: searchFilters.email.trim().toLowerCase(),
      managerFullName: searchFilters.managerFullName.trim().toLowerCase(),
    };

    return users
      .filter((user) => {
        return (
          matchesEmployeeStatus(
            user.status === "ACTIVE" || user.status === "INACTIVE"
              ? user.status
              : undefined,
            activeFilter,
          ) &&
          // Current code or the code of any earlier employment.
          [user.employeeCode, ...(user.previousEmployeeCodes ?? [])].some(
            (code) =>
              String(code || "")
                .toLowerCase()
                .includes(normalizedFilters.employeeCode),
          ) &&
          String(user.firstName || "")
            .toLowerCase()
            .includes(normalizedFilters.firstName) &&
          String(user.lastName || "")
            .toLowerCase()
            .includes(normalizedFilters.lastName) &&
          String(user.email || "")
            .toLowerCase()
            .includes(normalizedFilters.email) &&
          String(user.managerFullName || "")
            .toLowerCase()
            .includes(normalizedFilters.managerFullName)
        );
      })
      .sort((a, b) => {
        const lastNameCompare = (a.lastName || "").localeCompare(
          b.lastName || "",
          undefined,
          { sensitivity: "base" },
        );

        if (lastNameCompare !== 0) {
          return lastNameCompare;
        }

        return (a.firstName || "").localeCompare(b.firstName || "", undefined, {
          sensitivity: "base",
        });
      });
  }, [users, searchFilters, activeFilter]);

  const paginatedUsers = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredSortedUsers.slice(start, start + pageSize);
  }, [filteredSortedUsers, currentPage, pageSize]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchFilters, activeFilter, pageSize]);

  useEffect(() => {
    const totalPages = Math.max(
      1,
      Math.ceil(filteredSortedUsers.length / pageSize),
    );

    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [filteredSortedUsers.length, currentPage, pageSize]);

  const columns = useMemo<ColumnsType<User>>(
    () => [
      {
        title: "Employee Code",
        dataIndex: "employeeCode",
        key: "employeeCode",
      },
      {
        title: "Firstname",
        dataIndex: "firstName",
        key: "firstName",
      },
      {
        title: "Lastname",
        dataIndex: "lastName",
        key: "lastName",
      },
      {
        title: "Email",
        dataIndex: "email",
        key: "email",
      },
      {
        title: "Manager",
        dataIndex: "managerFullName",
        key: "managerFullName",
      },
      {
        // Follows the employment dates: Active up to and including the
        // termination date, Inactive (no access) from the next day.
        title: "Status",
        key: "status",
        render: (_: unknown, record: User) => {
          const isActive = record.status === "ACTIVE";
          const today = dayjs().startOf("day");
          const start = record.employmentStartDate
            ? dayjs(record.employmentStartDate)
            : null;
          const end = record.employmentEndDate
            ? dayjs(record.employmentEndDate)
            : null;
          let detail: string | null = null;
          if (isActive && end) {
            detail = `until ${end.format("DD.MM.YYYY")}`;
          } else if (!isActive && start && start.isAfter(today)) {
            detail = `from ${start.format("DD.MM.YYYY")}`;
          } else if (!isActive && end) {
            detail = `since ${end.add(1, "day").format("DD.MM.YYYY")}`;
          }
          return (
            <div>
              <Tag color={isActive ? "green" : "red"}>
                {isActive ? "Active" : "Inactive"}
              </Tag>
              {detail && (
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  {detail}
                </Typography.Text>
              )}
            </div>
          );
        },
      },
      {
        title: "Actions",
        key: "actions",
        width: 220,
        fixed: isDesktop ? "right" : undefined,
        align: "center",
        render: (_: unknown, record: User) => (
          <div className="action-buttons">
            <Tooltip title="View user details">
              <Button
                icon={<EyeOutlined />}
                onClick={() => {
                  setSelectedUser(record);
                  setIsViewUserModalOpen(true);
                }}
              />
            </Tooltip>
            <Tooltip title="Edit user">
              <Button
                icon={<EditOutlined />}
                onClick={() => {
                  setSelectedUser(record);
                  setIsModalOpen(true);
                }}
              />
            </Tooltip>
            <Tooltip title="Employment history">
              <Button
                icon={<HistoryOutlined />}
                onClick={() => {
                  setSelectedUser(record);
                  setIsHistoryModalOpen(true);
                }}
              />
            </Tooltip>
            {record.hasOpenEmployment ? (
              <Tooltip title="Terminate employment">
                <Button
                  danger
                  icon={<StopOutlined />}
                  onClick={() => {
                    setSelectedUser(record);
                    setIsTerminateModalOpen(true);
                  }}
                />
              </Tooltip>
            ) : (
              <Tooltip title="Change or remove the termination date">
                <Button
                  danger
                  icon={<CalendarOutlined />}
                  onClick={() => {
                    setSelectedUser(record);
                    setIsTerminateModalOpen(true);
                  }}
                />
              </Tooltip>
            )}
            {!record.hasOpenEmployment && record.status === "INACTIVE" && (
              <Tooltip title="Rehire / new employment">
                <Button
                  icon={<UserAddOutlined />}
                  onClick={() => {
                    setSelectedUser(record);
                    setIsRehireModalOpen(true);
                  }}
                />
              </Tooltip>
            )}
          </div>
        ),
      },
    ],
    [isDesktop],
  );

  return (
    <div className="users">
      <Typography.Title level={4}>User Management</Typography.Title>
      <UserModal
        isOpen={isModalOpen}
        setIsOpen={setIsModalOpen}
        user={selectedUser}
        refreshData={fetchUsers}
      />
      {selectedUser && (
        <ViewUserDetailsModal
          isOpen={isViewUserModalOpen}
          setIsOpen={setIsViewUserModalOpen}
          user={selectedUser}
        />
      )}
      {selectedUser && (
        <TerminateEmploymentModal
          isOpen={isTerminateModalOpen}
          setIsOpen={setIsTerminateModalOpen}
          user={selectedUser}
          refreshData={fetchUsers}
        />
      )}
      {selectedUser && (
        <RehireModal
          isOpen={isRehireModalOpen}
          setIsOpen={setIsRehireModalOpen}
          user={selectedUser}
          refreshData={fetchUsers}
        />
      )}
      {selectedUser && (
        <EmploymentHistoryModal
          isOpen={isHistoryModalOpen}
          setIsOpen={setIsHistoryModalOpen}
          user={selectedUser}
          refreshData={fetchUsers}
        />
      )}
      <Button
        title="Add user"
        className="new-company-btn"
        onClick={() => {
          setSelectedUser(null);
          setIsModalOpen(true);
        }}
        type="primary"
        icon={<PlusOutlined />}
      >
        Add user
      </Button>

      <div className="table-container">
        <div className="table-filters">
          <Input
            allowClear
            placeholder="Search employee code"
            value={searchFilters.employeeCode}
            onChange={(event) =>
              handleSearchFilterChange("employeeCode", event.target.value)
            }
          />
          <Input
            allowClear
            placeholder="Search first name"
            value={searchFilters.firstName}
            onChange={(event) =>
              handleSearchFilterChange("firstName", event.target.value)
            }
          />
          <Input
            allowClear
            placeholder="Search last name"
            value={searchFilters.lastName}
            onChange={(event) =>
              handleSearchFilterChange("lastName", event.target.value)
            }
          />
          <Input
            allowClear
            placeholder="Search email"
            value={searchFilters.email}
            onChange={(event) =>
              handleSearchFilterChange("email", event.target.value)
            }
          />
          <Input
            allowClear
            placeholder="Search manager"
            value={searchFilters.managerFullName}
            onChange={(event) =>
              handleSearchFilterChange("managerFullName", event.target.value)
            }
          />
          <EmployeeStatusFilter value={activeFilter} onChange={setActiveFilter} />
        </div>
        <Table
          className="view-table"
          columns={columns}
          scroll={{ x: true, y: fixedTableScrollY }}
          dataSource={paginatedUsers}
          rowKey={(record) => `${record.id}`}
          pagination={false}
          loading={{ spinning: loading, tip: "Loading users..." }}
          locale={{
            emptyText: <Empty description="No users found." />,
          }}
        />
        {filteredSortedUsers.length > 0 && (
          <Typography.Text className="entries-summary">
            Displaying {paginatedUsers.length} of {filteredSortedUsers.length}{" "}
            users
          </Typography.Text>
        )}
        {filteredSortedUsers.length > 0 && (
          <Pagination
            className="table-pagination"
            current={currentPage}
            total={filteredSortedUsers.length}
            pageSize={pageSize}
            pageSizeOptions={USERS_PAGE_SIZE_OPTIONS}
            onChange={(page, size) => {
              setCurrentPage(page);
              setPageSize(size);
            }}
            onShowSizeChange={(_, size) => {
              setCurrentPage(1);
              setPageSize(size);
            }}
            showTotal={(total, range) =>
              `${range[0]}-${range[1]} of ${total} users`
            }
            size="small"
            showSizeChanger
          />
        )}
      </div>
    </div>
  );
};

export default Users;
